// ============================================================================
// NotificationBell - header dropdown with unread count
// Drop into Header.jsx
// ============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { Badge, Dropdown, List, Button, Empty, Typography, Space, Tag } from 'antd';
import { BellOutlined, CheckOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../config';

const { Text } = Typography;

const apiBase = typeof API_BASE_URL === 'string' ? API_BASE_URL : (API_BASE_URL?.API_BASE_URL || '');

const PRIORITY_COLORS = { LOW: 'default', MEDIUM: 'blue', HIGH: 'orange', URGENT: 'red' };

const authedFetch = (path, opts = {}) =>
  fetch(`${apiBase}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      ...(opts.headers || {}),
    },
  });

const formatRelative = (date) => {
  const diff = (Date.now() - new Date(date).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(date).toLocaleDateString();
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadCount = useCallback(async () => {
    try {
      const res = await authedFetch('/api/notifications/unread-count');
      if (!res.ok) return;
      const data = await res.json();
      setUnread(data.count || 0);
    } catch { /* silent */ }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authedFetch('/api/notifications?limit=15');
      const data = await res.json();
      setItems(data.rows || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('token')) return;
    loadCount();
    const t = setInterval(loadCount, 60000);  // poll every 60s
    return () => clearInterval(t);
  }, [loadCount]);

  const onOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) loadItems();
  };

  const markRead = async (id) => {
    await authedFetch(`/api/notifications/${id}/read`, { method: 'POST' });
    setItems(items.map(i => i.id === id ? { ...i, isRead: true } : i));
    loadCount();
  };

  const markAllRead = async () => {
    await authedFetch('/api/notifications/mark-all-read', { method: 'POST' });
    setItems(items.map(i => ({ ...i, isRead: true })));
    setUnread(0);
  };

  const clickItem = (n) => {
    if (!n.isRead) markRead(n.id);
    if (n.actionUrl) {
      setOpen(false);
      navigate(n.actionUrl);
    }
  };

  const dropdownContent = (
    <div style={{ width: 380, maxHeight: 480, overflow: 'auto', background: '#fff',
                  borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,0.12)' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>Notifications</strong>
        {unread > 0 && (
          <Button type="link" size="small" icon={<CheckOutlined />} onClick={markAllRead}>
            Mark all read
          </Button>
        )}
      </div>
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center' }}><Text type="secondary">Loading…</Text></div>
      ) : items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notifications" style={{ padding: 32 }} />
      ) : (
        <List
          dataSource={items}
          renderItem={n => (
            <List.Item style={{
              padding: '10px 16px',
              background: n.isRead ? '#fff' : '#f0f9ff',
              cursor: n.actionUrl ? 'pointer' : 'default',
              borderBottom: '1px solid #f1f5f9',
            }} onClick={() => clickItem(n)}>
              <List.Item.Meta
                title={
                  <Space size={6}>
                    {!n.isRead && <span style={{ width: 8, height: 8, borderRadius: 4, background: '#3b82f6', display: 'inline-block' }} />}
                    <span style={{ fontWeight: n.isRead ? 400 : 600 }}>{n.title}</span>
                    {n.priority && n.priority !== 'MEDIUM' && (
                      <Tag color={PRIORITY_COLORS[n.priority]} style={{ fontSize: 10 }}>{n.priority}</Tag>
                    )}
                  </Space>
                }
                description={
                  <div>
                    <Text style={{ fontSize: 13, color: '#475569' }}>{n.message}</Text>
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>{formatRelative(n.createdAt)}</Text>
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <Dropdown trigger={['click']} open={open} onOpenChange={onOpenChange}
      dropdownRender={() => dropdownContent} placement="bottomRight">
      <Badge count={unread} size="small" offset={[-4, 4]}>
        <Button type="text" icon={<BellOutlined style={{ fontSize: 18 }} />}
          style={{ height: 36, width: 36 }} />
      </Badge>
    </Dropdown>
  );
};

export default NotificationBell;
